import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Key, Facebook, Twitter, Instagram, Linkedin, Youtube, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getSettings, updateSettings, getAbout, updateAbout } from '../../lib/api';
import ImageUpload from '../../components/common/ImageUpload';

const AdminSettings = () => {
  const { t } = useLanguage();
  const { changePassword } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    site_name_ar: '', site_name_en: '', logo_url: '',
    footer_text_ar: '', footer_text_en: '',
    contact_email: '', contact_phone: '',
    address_ar: '', address_en: '',
    map_lat: 33.5138, map_lng: 36.2765,
    facebook_url: '', twitter_url: '', instagram_url: '',
    linkedin_url: '', youtube_url: '', whatsapp_number: ''
  });
  const [about, setAbout] = useState({ title_ar: '', title_en: '', content_ar: '', content_en: '', image_url: '' });
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });

  const loadData = async () => {
    try {
      const [sRes, aRes] = await Promise.all([getSettings(), getAbout()]);
      setSettings(sRes.data);
      setAbout(aRes.data);
    } catch (error) {
      toast.error(t('خطأ', 'Error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleSaveSettings = async () => {
    setSubmitting(true);
    try {
      await updateSettings(settings);
      toast.success(t('تم حفظ الإعدادات', 'Settings saved'));
    } catch (error) {
      toast.error(t('خطأ', 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAbout = async () => {
    setSubmitting(true);
    try {
      await updateAbout(about);
      toast.success(t('تم حفظ المعلومات', 'Info saved'));
    } catch (error) {
      toast.error(t('خطأ', 'Error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new !== passwordData.confirm) {
      toast.error(t('كلمات المرور غير متطابقة', 'Passwords do not match'));
      return;
    }
    if (passwordData.new.length < 4) {
      toast.error(t('كلمة المرور قصيرة جداً', 'Password too short'));
      return;
    }
    try {
      await changePassword(passwordData.current, passwordData.new);
      toast.success(t('تم تغيير كلمة المرور', 'Password changed'));
      setPasswordDialogOpen(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(t('كلمة المرور الحالية غير صحيحة', 'Current password is incorrect'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-raad-rose border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{t('الإعدادات', 'Settings')}</h1>
          <p className="text-gray-400">{t('إعدادات الموقع العامة', 'General site settings')}</p>
        </div>
        <Button onClick={() => setPasswordDialogOpen(true)} variant="outline" className="border-white/20 text-white hover:bg-white/10">
          <Key className="w-4 h-4 me-2" />
          {t('تغيير كلمة المرور', 'Change Password')}
        </Button>
      </div>

      <Tabs defaultValue="site" className="space-y-6">
        <TabsList className="bg-raad-card border border-white/10">
          <TabsTrigger value="site" className="data-[state=active]:bg-gradient-raad">{t('إعدادات الموقع', 'Site Settings')}</TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-gradient-raad">{t('التواصل الاجتماعي', 'Social Media')}</TabsTrigger>
          <TabsTrigger value="about" className="data-[state=active]:bg-gradient-raad">{t('صفحة من نحن', 'About Page')}</TabsTrigger>
        </TabsList>

        {/* Site Settings Tab */}
        <TabsContent value="site">
          <Card className="bg-raad-card border-white/10">
            <CardHeader><CardTitle className="text-white">{t('إعدادات الموقع', 'Site Settings')}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('اسم الموقع (عربي)', 'Site Name (Arabic)')}</Label>
                  <Input value={settings.site_name_ar} onChange={(e) => setSettings({ ...settings, site_name_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" />
                </div>
                <div>
                  <Label className="text-gray-300">{t('اسم الموقع (إنجليزي)', 'Site Name (English)')}</Label>
                  <Input value={settings.site_name_en} onChange={(e) => setSettings({ ...settings, site_name_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" />
                </div>
              </div>
              
              <div>
                <ImageUpload
                  label={t('شعار الموقع', 'Site Logo')}
                  value={settings.logo_url}
                  onChange={(url) => setSettings({ ...settings, logo_url: url })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('البريد الإلكتروني', 'Email')}</Label>
                  <Input value={settings.contact_email} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">{t('رقم الهاتف', 'Phone')}</Label>
                  <Input value={settings.contact_phone} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 phone-ltr" dir="ltr" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('العنوان (عربي)', 'Address (Arabic)')}</Label>
                  <Textarea value={settings.address_ar} onChange={(e) => setSettings({ ...settings, address_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" rows={2} />
                </div>
                <div>
                  <Label className="text-gray-300">{t('العنوان (إنجليزي)', 'Address (English)')}</Label>
                  <Textarea value={settings.address_en} onChange={(e) => setSettings({ ...settings, address_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" rows={2} />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('خط العرض', 'Latitude')}</Label>
                  <Input type="number" step="any" value={settings.map_lat} onChange={(e) => setSettings({ ...settings, map_lat: parseFloat(e.target.value) })} className="bg-white/5 border-white/10 text-white mt-1" />
                </div>
                <div>
                  <Label className="text-gray-300">{t('خط الطول', 'Longitude')}</Label>
                  <Input type="number" step="any" value={settings.map_lng} onChange={(e) => setSettings({ ...settings, map_lng: parseFloat(e.target.value) })} className="bg-white/5 border-white/10 text-white mt-1" />
                </div>
              </div>
              
              <Button onClick={handleSaveSettings} disabled={submitting} className="bg-gradient-raad">
                <Save className="w-4 h-4 me-2" />
                {submitting ? t('جاري الحفظ...', 'Saving...') : t('حفظ الإعدادات', 'Save Settings')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social">
          <Card className="bg-raad-card border-white/10">
            <CardHeader><CardTitle className="text-white">{t('روابط التواصل الاجتماعي', 'Social Media Links')}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-blue-500" />
                    Facebook
                  </Label>
                  <Input value={settings.facebook_url} onChange={(e) => setSettings({ ...settings, facebook_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" placeholder="https://facebook.com/..." />
                </div>
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Twitter className="w-4 h-4 text-sky-500" />
                    Twitter / X
                  </Label>
                  <Input value={settings.twitter_url} onChange={(e) => setSettings({ ...settings, twitter_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" placeholder="https://twitter.com/..." />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-pink-500" />
                    Instagram
                  </Label>
                  <Input value={settings.instagram_url} onChange={(e) => setSettings({ ...settings, instagram_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" placeholder="https://instagram.com/..." />
                </div>
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Linkedin className="w-4 h-4 text-blue-600" />
                    LinkedIn
                  </Label>
                  <Input value={settings.linkedin_url} onChange={(e) => setSettings({ ...settings, linkedin_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" placeholder="https://linkedin.com/company/..." />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-500" />
                    YouTube
                  </Label>
                  <Input value={settings.youtube_url} onChange={(e) => setSettings({ ...settings, youtube_url: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" placeholder="https://youtube.com/..." />
                </div>
                <div>
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-green-500" />
                    WhatsApp
                  </Label>
                  <Input value={settings.whatsapp_number} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1 phone-ltr" dir="ltr" placeholder="+963912345678" />
                </div>
              </div>
              
              <Button onClick={handleSaveSettings} disabled={submitting} className="bg-gradient-raad">
                <Save className="w-4 h-4 me-2" />
                {submitting ? t('جاري الحفظ...', 'Saving...') : t('حفظ الإعدادات', 'Save Settings')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Tab */}
        <TabsContent value="about">
          <Card className="bg-raad-card border-white/10">
            <CardHeader><CardTitle className="text-white">{t('صفحة من نحن', 'About Page')}</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('العنوان (عربي)', 'Title (Arabic)')}</Label>
                  <Input value={about.title_ar} onChange={(e) => setAbout({ ...about, title_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" />
                </div>
                <div>
                  <Label className="text-gray-300">{t('العنوان (إنجليزي)', 'Title (English)')}</Label>
                  <Input value={about.title_en} onChange={(e) => setAbout({ ...about, title_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">{t('المحتوى (عربي)', 'Content (Arabic)')}</Label>
                  <Textarea value={about.content_ar} onChange={(e) => setAbout({ ...about, content_ar: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="rtl" rows={5} />
                </div>
                <div>
                  <Label className="text-gray-300">{t('المحتوى (إنجليزي)', 'Content (English)')}</Label>
                  <Textarea value={about.content_en} onChange={(e) => setAbout({ ...about, content_en: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" dir="ltr" rows={5} />
                </div>
              </div>
              
              <div>
                <ImageUpload
                  label={t('صورة القسم', 'Section Image')}
                  value={about.image_url}
                  onChange={(url) => setAbout({ ...about, image_url: url })}
                />
              </div>
              
              <Button onClick={handleSaveAbout} disabled={submitting} className="bg-gradient-raad">
                <Save className="w-4 h-4 me-2" />
                {submitting ? t('جاري الحفظ...', 'Saving...') : t('حفظ المعلومات', 'Save Info')}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="bg-raad-card border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{t('تغيير كلمة المرور', 'Change Password')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="text-gray-300">{t('كلمة المرور الحالية', 'Current Password')}</Label>
              <Input type="password" value={passwordData.current} onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-gray-300">{t('كلمة المرور الجديدة', 'New Password')}</Label>
              <Input type="password" value={passwordData.new} onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-gray-300">{t('تأكيد كلمة المرور', 'Confirm Password')}</Label>
              <Input type="password" value={passwordData.confirm} onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPasswordDialogOpen(false)}>{t('إلغاء', 'Cancel')}</Button>
              <Button type="submit" className="bg-gradient-raad">{t('تغيير', 'Change')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
